/**
 * PATH: packages/ui/src/components/sidebar/SidebarHeader.tsx
 * PURPOSE: Hub icon, name switcher dropdown, and customize button
 */

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDown,
  LayoutDashboard,
  Search,
  Command,
  SlidersHorizontal,
  Home,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { cn } from '../../lib/utils'
import type { ExternalLink, ModuleConfig } from '../types'

interface SidebarHeaderProps {
  module: ModuleConfig
  collapsed: boolean
  externalLinks?: (ExternalLink & { status?: 'production' | 'development' | 'planned' })[]
  onOpenSearch?: () => void
  onOpenCustomize?: () => void
  onHubClick?: (hubId: string, href: string) => void
  collapsedHubHeaderButtonRef: React.RefObject<HTMLButtonElement | null>
  onToggleCollapsedHubs: (anchor: 'header' | 'grid') => (event: React.MouseEvent<HTMLButtonElement>) => void
}

export function SidebarHeader({
  module,
  collapsed,
  externalLinks,
  onOpenSearch,
  onOpenCustomize,
  onHubClick,
  collapsedHubHeaderButtonRef,
  onToggleCollapsedHubs,
}: SidebarHeaderProps) {
  const [hubSwitcherOpen, setHubSwitcherOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const hubSwitcherRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const ModuleIcon = module.icon || LayoutDashboard
  const accentGradient = module.accentGradient || 'from-teal-500 to-teal-600'
  // No hubs (e.g. host set `hideExternalLinks` because hubs now live in
  // search): drop the switcher affordances so the header doesn't show a
  // dead chevron / no-op "Switch Hub" button.
  const hasHubs = !!externalLinks && externalLinks.length > 0

  // "Back to Dashboard" home link (operator 2026-06-13: "I can't ... go back
  // to home" from a hub). Standalone hub shells (cfo.quotech.ai, qa.quotech.ai,
  // …) render this header but, since cross-hub links moved into search, have
  // no visible way back to the master dashboard. Gate purely on hostname so
  // this is a NO-OP on the master shell (dashboard.quotech.ai — already home)
  // and in dev/storybook/localhost (not a *.quotech.ai subdomain), and needs
  // no per-hub prop wiring. The master shell picks up the shared quotech_auth
  // cookie, so this is a seamless same-domain SSO hop.
  const dashboardHome = useMemo<string | null>(() => {
    if (typeof window === 'undefined') return null
    const host = window.location.hostname
    if (host === 'dashboard.quotech.ai') return null
    if (!host.endsWith('.quotech.ai')) return null
    return 'https://dashboard.quotech.ai'
  }, [])

  // Close hub switcher on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedInTrigger = hubSwitcherRef.current?.contains(event.target as Node)
      const clickedInDropdown = dropdownRef.current?.contains(event.target as Node)
      if (!clickedInTrigger && !clickedInDropdown) {
        setHubSwitcherOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggleDropdown = useCallback(() => {
    if (!hubSwitcherOpen && hubSwitcherRef.current) {
      const rect = hubSwitcherRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left })
    }
    setHubSwitcherOpen(prev => !prev)
  }, [hubSwitcherOpen])

  return (
    <>
      {/* Back to main dashboard — standalone hub shells only (see dashboardHome) */}
      {dashboardHome && (
        <div className={cn('border-b border-border', collapsed ? 'p-2 flex justify-center' : 'px-3 pt-2 pb-1.5')}>
          {collapsed ? (
            <a href={dashboardHome} title="Back to Dashboard"
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
              <Home className="w-5 h-5" />
            </a>
          ) : (
            <a href={dashboardHome}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors">
              <Home className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">Back to Dashboard</span>
            </a>
          )}
        </div>
      )}

      {/* Hub Header */}
      <div className={cn('border-b border-border', collapsed ? 'p-2' : 'px-3 py-3')}>
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2')}>
          {collapsed ? (
            <div className="relative">
              {hasHubs ? (
                <button ref={collapsedHubHeaderButtonRef} onClick={onToggleCollapsedHubs('header')}
                  className={cn('flex items-center justify-center rounded-xl bg-gradient-to-br flex-shrink-0 shadow-sm transition-all hover:scale-105 hover:shadow-md cursor-pointer', accentGradient, 'w-10 h-10')}
                  title="Switch Hub">
                  <ModuleIcon className="w-5 h-5 text-white" />
                </button>
              ) : (
                <div
                  className={cn('flex items-center justify-center rounded-xl bg-gradient-to-br flex-shrink-0 shadow-sm', accentGradient, 'w-10 h-10')}
                  title={module.name}>
                  <ModuleIcon className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          ) : (
            <div className={cn('flex items-center justify-center rounded-xl bg-gradient-to-br flex-shrink-0 shadow-sm', accentGradient, 'w-9 h-9')}>
              <ModuleIcon className="w-4.5 h-4.5 text-white" />
            </div>
          )}

          {!collapsed && (
            <>
              <div className="flex-1 min-w-0" ref={hubSwitcherRef}>
                {hasHubs ? (
                  <button onClick={handleToggleDropdown}
                    className="flex items-center gap-1 hover:bg-muted rounded-md px-1.5 py-1 transition-colors w-full">
                    <span className="text-[13px] font-semibold text-foreground truncate">{module.name}</span>
                    <ChevronDown className={cn('w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0', hubSwitcherOpen && 'rotate-180')} />
                  </button>
                ) : (
                  <div className="px-1.5 py-1">
                    <span className="text-[13px] font-semibold text-foreground truncate">{module.name}</span>
                  </div>
                )}

                {hubSwitcherOpen && externalLinks && externalLinks.length > 0 && createPortal(
                  /* Defence-in-depth: the menu's primary background colour is
                     `bg-popover` so it adopts each hub's theme. But if a
                     consumer hub forgets to declare `--popover` in its
                     :root (the failure mode behind
                     BUG-2026-05-22-DEV-HUB-TRANSPARENT-DROPDOWN), `bg-popover`
                     resolves to nothing → fully transparent menu →
                     the page content bleeds through behind the items. The
                     inline `backgroundColor` below is the floor: an opaque
                     white in light mode, dark slate in dark mode, never
                     transparent. `bg-popover` still overrides it on
                     properly-themed hubs so the visual stays consistent. */
                  <div ref={dropdownRef}
                    style={{
                      position: 'fixed',
                      top: dropdownPos.top,
                      left: dropdownPos.left,
                      backgroundColor: 'var(--popover, var(--color-popover, var(--card, #ffffff)))',
                    }}
                    className="w-64 bg-popover border border-border rounded-xl shadow-2xl z-[9999] py-1.5 max-h-80 overflow-y-auto">
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">All Hubs</p>
                    {externalLinks.map((link) => (
                      <a key={link.id || link.href} href={link.href}
                        onClick={(e) => {
                          if (link.status === 'planned') { e.preventDefault(); setHubSwitcherOpen(false); return }
                          if (onHubClick && link.id) { e.preventDefault(); onHubClick(link.id, link.href); setHubSwitcherOpen(false); return }
                          setHubSwitcherOpen(false)
                        }}
                        className={cn('flex items-center gap-2 px-3 py-2 text-[12px] transition-colors',
                          link.status === 'planned' ? 'text-muted-foreground hover:bg-muted'
                          : link.status === 'development' ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                          : 'text-muted-foreground hover:bg-muted')}>
                        <link.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate flex-1">{link.label}</span>
                        {link.status === 'development' && <span className="px-1.5 py-0.5 text-[8px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full">DEV</span>}
                        {link.status === 'planned' && <span className="px-1.5 py-0.5 text-[8px] font-medium bg-muted text-muted-foreground rounded-full">SOON</span>}
                      </a>
                    ))}
                  </div>,
                  document.body
                )}
              </div>

              {onOpenCustomize && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={onOpenCustomize}
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors flex-shrink-0">
                        <SlidersHorizontal className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      <p className="font-medium">Settings & Customization</p>
                      <p className="text-muted-foreground">Navigation, appearance & preferences</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search bar */}
      {onOpenSearch && (
        <div className={cn('px-2 py-2', collapsed && 'flex justify-center')}>
          {collapsed ? (
            <button onClick={onOpenSearch} title="Search (⌘K)"
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors">
              <Search className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={onOpenSearch}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-muted-foreground bg-muted rounded-md border border-border hover:bg-accent transition-colors">
              <Search className="w-3.5 h-3.5" />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="flex items-center gap-0.5 px-1 py-0.5 bg-card border border-border rounded text-[10px]">
                <Command className="w-2.5 h-2.5" />K
              </kbd>
            </button>
          )}
        </div>
      )}
    </>
  )
}
